import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import Confetti from 'react-confetti';

const API_URL = process.env.REACT_APP_API_URL || 'https://localhost:50586/api/';

const formatAssetUrl = (url, fallback = '') => {
    if (!url) return fallback;
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
    if (url.startsWith('/img/') || url.startsWith('/static/') || url.startsWith('static/')) return url;
    const baseUrl = API_URL.replace(/\/api\/?$/, '');
    return `${baseUrl}${url.startsWith('/') ? '' : '/'}${url}`;
};

const PluckingRooster = ({ data, initialSettings, roomId: propRoomId }) => {
    const [isVisible, setIsVisible] = useState(true);
    const [bannerMode, setBannerMode] = useState('invitation'); // invitation, ranking, winner
    const [offsetY, setOffsetY] = useState(0);
    const [rankingList, setRankingList] = useState([]);
    const [winnerName, setWinnerName] = useState('');
    const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });

    const effectiveRoomId = propRoomId || initialSettings?.roomId || new URLSearchParams(window.location.search).get('roomId');
    const qrUrl = `${window.location.origin}/rooster${effectiveRoomId ? `?roomId=${encodeURIComponent(effectiveRoomId)}` : ''}`;

    useEffect(() => {
        const handleResize = () => setWindowSize({ width: window.innerWidth, height: window.innerHeight });
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (initialSettings) {
            if (initialSettings.isOverlayVisible !== undefined) setIsVisible(initialSettings.isOverlayVisible);
            if (initialSettings.bannerMode) setBannerMode(initialSettings.bannerMode);
            if (initialSettings.offsetY !== undefined) setOffsetY(initialSettings.offsetY);
            if (initialSettings.winnerName) setWinnerName(initialSettings.winnerName);
            if (Array.isArray(initialSettings.rankingList)) setRankingList(initialSettings.rankingList);
        }
    }, [initialSettings]);

    useEffect(() => {
        if (!data) return;

        const subtype = (data.subtype || data.action || data.type || '').toLowerCase();
        const payload = data.data || data.payload || data;

        if (payload) {
            if (payload.bannerMode) setBannerMode(payload.bannerMode);
            if (payload.mode) setBannerMode(payload.mode);
            if (payload.offsetY !== undefined) setOffsetY(payload.offsetY);
            if (payload.positionOffset !== undefined) setOffsetY(payload.positionOffset);
            if (payload.isOverlayVisible !== undefined) setIsVisible(payload.isOverlayVisible);
            if (payload.visible !== undefined) setIsVisible(payload.visible);
            if (payload.winnerName) setWinnerName(payload.winnerName);
            if (Array.isArray(payload.rankingList)) setRankingList(payload.rankingList);
        }

        if (subtype.includes('show')) setIsVisible(true);
        if (subtype.includes('hide')) setIsVisible(false);
    }, [data]);

    const winnerPlayer = rankingList.find(p => p.isWinner) || rankingList[0];
    const displayWinnerName = winnerName || winnerPlayer?.fullName || winnerPlayer?.name || 'Aficionado Ganador';
    const displayWinnerScore = winnerPlayer?.score || 0;

    const sponsorLogo = initialSettings?.customConfig?.sponsorLogo
        ? formatAssetUrl(initialSettings.customConfig.sponsorLogo)
        : "/img/page/xolos-five.png";

    const containerClass = `rooster-overlay ${isVisible ? 'rooster-overlay--visible' : ''} rooster-overlay--horizontal`;

    const positionStyle = {
        position: 'fixed',
        bottom: `${offsetY + 25}px`,
        left: '50%',
        transform: isVisible ? `translateX(-50%) translateY(0)` : `translateX(-50%) translateY(150px)`,
        opacity: isVisible ? 1 : 0,
        transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
        zIndex: 999,
        width: '90%',
        maxWidth: '1200px'
    };

    return (
        <>
            {/* GPU Confetti en vivo cuando el banner de Ganador está activo */}
            {isVisible && bannerMode === 'winner' && (
                <Confetti 
                    width={windowSize.width} 
                    height={windowSize.height} 
                    numberOfPieces={350} 
                    recycle={true}
                    run={true}
                    colors={['#ffd700', '#f97316', '#ef4444', '#3b82f6', '#10b981', '#ffffff']}
                />
            )}

            <div className={containerClass} style={positionStyle}>
                
                {/* 1. MODO INVITACIÓN: QR + Mensaje de Invitación */}
                {bannerMode === 'invitation' && (
                    <div className="d-flex align-items-center justify-content-between w-100 bg-dark text-white p-3 rounded-4 border border-warning shadow-lg" style={{ background: 'linear-gradient(90deg, #111827 0%, #1f2937 100%)' }}>
                        <div className="d-flex align-items-center gap-3">
                            <img src={sponsorLogo} alt="Logo Patrocinador" style={{ maxHeight: '55px', maxWidth: '140px', objectFit: 'contain' }} />
                            <div>
                                <h3 className="fw-black text-warning text-uppercase mb-0 fs-4" style={{ letterSpacing: '1px', textShadow: '1px 1px 4px rgba(0,0,0,0.8)' }}>
                                    ¡JUEGA PÉGALE AL PERSONAJE!
                                </h3>
                                <p className="mb-0 text-white-50 fs-6 fw-bold">
                                    ESCANEA EL CÓDIGO QR Y PARTICIPA POR INCREÍBLES PREMIOS
                                </p>
                            </div>
                        </div>

                        <div className="bg-white p-2 rounded-3 ms-3 shadow d-flex align-items-center justify-content-center">
                            <QRCodeSVG value={qrUrl} size={90} level="M" />
                        </div>
                    </div>
                )}

                {/* 2. MODO RANKING: Top 5 Resumido con Animación Cascading SlideIn */}
                {bannerMode === 'ranking' && (
                    <div className="w-100 bg-dark text-white p-3 rounded-4 border border-warning shadow-lg" style={{ background: 'linear-gradient(90deg, #111827 0%, #1f2937 100%)' }}>
                        <div className="d-flex align-items-center justify-content-between mb-2 pb-2 border-bottom border-secondary">
                            <div className="d-flex align-items-center gap-2">
                                <span className="fs-4">🏆</span>
                                <h4 className="fw-black text-warning text-uppercase mb-0 fs-5" style={{ textShadow: '1px 1px 4px rgba(0,0,0,0.8)' }}>
                                    TOP 5 MEJORES PUNTUACIONES
                                </h4>
                            </div>
                            <span className="badge bg-warning text-dark fw-bold text-uppercase">Pégale al Personaje</span>
                        </div>

                        <div className="d-flex flex-row gap-2 overflow-hidden w-100">
                            {rankingList.slice(0, 5).map((player, index) => (
                                <div 
                                    key={player.id || index} 
                                    className="flex-fill bg-secondary bg-opacity-25 border border-secondary rounded-3 p-2 d-flex flex-column justify-content-between"
                                    style={{ 
                                        animation: `slideIn 0.4s ease-out forwards`,
                                        animationDelay: `${index * 0.1}s`,
                                        minWidth: '160px'
                                    }}
                                >
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <span className={`badge ${index === 0 ? 'bg-warning text-dark' : index === 1 ? 'bg-light text-dark' : index === 2 ? 'bg-danger text-white' : 'bg-dark text-white'}`}>
                                            #{index + 1}
                                        </span>
                                        <span className="fw-bold text-warning fs-6">{player.score || 0} pts</span>
                                    </div>
                                    <span className="fw-bold text-white text-truncate small">
                                        {player.fullName || player.name || 'Aficionado'}
                                    </span>
                                </div>
                            ))}

                            {rankingList.length === 0 && (
                                <div className="text-center text-white-50 w-100 py-2 italic small">
                                    Esperando puntuaciones en vivo...
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* 3. MODO WINNER: Tarjeta del Ganador con Confeti */}
                {bannerMode === 'winner' && (
                    <div className="d-flex align-items-center justify-content-between w-100 text-dark p-3 rounded-4 border border-white shadow-lg" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)' }}>
                        <div className="d-flex align-items-center gap-3">
                            <div className="bg-dark text-warning p-3 rounded-circle d-flex align-items-center justify-content-center shadow" style={{ width: '65px', height: '65px' }}>
                                <i className="bi bi-trophy-fill fs-2"></i>
                            </div>
                            <div>
                                <span className="badge bg-dark text-warning text-uppercase fw-bold px-2 py-1 mb-1 d-inline-block">
                                    🎉 AFICIONADO GANADOR 🎉
                                </span>
                                <h2 className="fw-black text-dark text-uppercase mb-0 fs-3" style={{ textShadow: '1px 1px 2px rgba(255,255,255,0.6)' }}>
                                    {displayWinnerName}
                                </h2>
                            </div>
                        </div>

                        <div className="bg-dark text-white px-4 py-2 rounded-3 text-center shadow ms-3">
                            <span className="d-block small text-warning fw-bold text-uppercase">Puntuación</span>
                            <span className="fs-3 fw-black text-warning">{displayWinnerScore} <small className="fs-6 text-white">PTS</small></span>
                        </div>
                    </div>
                )}

            </div>
        </>
    );
};

export { PluckingRooster };